import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import auth from './AuthService';
import 'react-datepicker/dist/react-datepicker.css';
import 'bootstrap/dist/css/bootstrap.css';

function EditAppointment() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    eventID: '',
    note: '',
    playerNames: [],
    idealStart: '',
    tsOpenCustom: '',
    tsOpen: '',
    userActionRequest: '',
    includeSelf: true,
    bookingFeedback: '',
    snipingLog: '',
    botLogs: [],
    joinSyndicate: false,
    bookingStatus: 'unclaimed',
  });
  const [userProfile, setUserProfile] = useState(null);
  const [logsExpanded, setLogsExpanded] = useState(true);

  useEffect(() => {
    auth.fetch('/accounts/profile').then(data => setUserProfile(data)).catch(() => {});
  }, []);

  const getEffectivePlayerNames = () => {
    let names = state.playerNames.filter(n => n.trim().length > 0);
    if (userProfile && userProfile.bookingName) {
      names = names.filter(n => n !== userProfile.bookingName);
      if (state.includeSelf) {
        names.unshift(userProfile.bookingName);
      }
    }
    return names;
  };

  // Called when playerNames changes (from load or edit) — maintain includeSelf invariant
  const setPlayerNames = (names) => {
    if (!userProfile || !userProfile.bookingName) {
      setState(prev => ({ ...prev, playerNames: names }));
      return;
    }
    const filtered = names.filter(n => n !== userProfile.bookingName);
    if (state.includeSelf) {
      filtered.unshift(userProfile.bookingName);
    }
    setState(prev => ({ ...prev, playerNames: filtered }));
  };

  useEffect(() => {
    if (!appointmentId) return;
    auth.fetch(`/appointments/${appointmentId}`, { method: 'GET' })
      .then(response => {
        if (response) {
          const names = response.playerNames || [];
          setState(prev => ({ ...prev, ...response, playerNames: names }));
        }
      })
      .catch(() => {
        setState(prev => ({ ...prev, eventID: appointmentId }));
      });
  }, [appointmentId]);

  // When includeSelf or userProfile changes, reconcile playerNames
  useEffect(() => {
    if (!userProfile) return;
    setPlayerNames(state.playerNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.includeSelf, userProfile]);

  const addPlayerRow = (fromIndex) => {
    setState(prev => {
      if (prev.playerNames.length >= 4) return prev;
      const newIndex = prev.playerNames.length;
      setTimeout(() => {
        const inputs = document.querySelectorAll('.player-name-input');
        if (inputs[newIndex]) inputs[newIndex].focus();
      }, 50);
      return { ...prev, playerNames: [...prev.playerNames, ''] };
    });
  };

  const removePlayerRow = (index) => {
    if (state.playerNames.length <= 1) return;
    setState(prev => {
      const names = [...prev.playerNames];
      names.splice(index, 1);
      return { ...prev, playerNames: names };
    });
  };

  const onChangePlayerName = (index, value) => {
    const names = [...state.playerNames];
    names[index] = value;
    setState(prev => ({ ...prev, playerNames: names }));
  };

  const onChangeIdealStart = (e) => setState((prev) => ({ ...prev, idealStart: e.target.value }));
  const onChangeTsOpenCustom = (e) => setState((prev) => ({ ...prev, tsOpenCustom: e.target.value }));
  const onChangeIncludeSelf = (e) => setState((prev) => ({ ...prev, includeSelf: e.target.checked }));
  const onChangeJoinSyndicate = (e) => setState((prev) => ({ ...prev, joinSyndicate: e.target.checked }));
  const onChangeNote = (e) => setState((prev) => ({ ...prev, note: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const playerNames = getEffectivePlayerNames();
    const tsOpenCustomLocal = state.tsOpenCustom === state.tsOpen ? '' : state.tsOpenCustom;
    const appointment = {
      appointmentId: state._id || undefined,
      eventID: state.eventID,
      playerNames,
      idealStart: state.idealStart,
      tsOpenCustom: tsOpenCustomLocal,
      joinSyndicate: state.joinSyndicate,
      note: state.note,
    };
    try {
      await auth.fetch('/appointments/create', {
        method: 'POST',
        body: JSON.stringify(appointment),
      });
      navigate('/');
    } catch (error) {
      console.error('Create appointment failed:', error);
    }
  };

  const onCancel = () => navigate('/');

  return (
    <div>
      <h3>Edit Appointment</h3>
      <form onSubmit={onSubmit}>
        <div className="form-group my-2">
          <div className="form-check form-check-inline">
            <label className="form-check-label mr-2">Include Self: </label>
            <input
              type="checkbox"
              className="form-check-input"
              checked={state.includeSelf}
              onChange={onChangeIncludeSelf}
            />
          </div>
        </div>
        {state.playerNames.map((name, i) => (
          <div className="form-group my-2" key={i}>
            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                className="form-control player-name-input"
                value={name}
                onChange={(e) => onChangePlayerName(i, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPlayerRow(i + 1); } }}
                placeholder="e.g. John Smith"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removePlayerRow(i)}
                disabled={state.playerNames.length <= 1}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addPlayerRow}>
          + Add Player
        </button>
        <div className="form-group my-2">
          <label>Ideal Start Time (h:mm): </label>
          <input
            type="text"
            className="form-control"
            value={state.idealStart}
            onChange={onChangeIdealStart}
          />
        </div>
        <div className="form-group my-2">
          <label>Custom Timesheet Opening: </label>
          <input
            type="text"
            className="form-control"
            value={state.tsOpenCustom}
            onChange={onChangeTsOpenCustom}
          />
        </div>
        <div className="form-group my-2">
          <label>Notes: </label>
          <input
            type="text"
            className="form-control"
            value={state.note}
            onChange={onChangeNote}
          />
        </div>
        <div className="form-group my-2">
          <div className="form-check">
            <label className="form-check-label">
              <input
                type="checkbox"
                className="form-check-input"
                checked={state.joinSyndicate}
                onChange={onChangeJoinSyndicate}
              />
              {' '}Join Syndicate — allow other syndicate bots to claim this appointment
            </label>
          </div>
        </div>
        {state.bookingStatus && state.bookingStatus !== 'unclaimed' && (
          <div className="form-group my-2">
            <label>Status: </label>
            <span className={`badge ${state.bookingStatus === 'booked' ? 'bg-success' : 'bg-warning'}`}>
              {state.bookingStatus}
            </span>
          </div>
        )}
        <div className="form-group my-2">
          <label>Booking Feedback: </label>
          <textarea
            className="form-control"
            value={state.bookingFeedback || ''}
            readOnly
            style={{ backgroundColor: '#f5f5f5' }}
          />
        </div>

        <div className="form-group" style={{ marginTop: '16px' }}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setLogsExpanded(!logsExpanded)}
            style={{ marginBottom: logsExpanded ? '8px' : '0' }}
          >
            {logsExpanded ? '▼ Hide Bot Logs' : `▶ Show Bot Logs (${state.botLogs?.length || 0})`}
          </button>
          {logsExpanded && state.botLogs && state.botLogs.length > 0 && (
            <div
              style={{
                background: '#1e1e1e',
                color: '#0f0',
                fontFamily: 'monospace',
                fontSize: '12px',
                padding: '10px',
                borderRadius: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                marginTop: '8px',
              }}
            >
              {state.botLogs.map((entry, i) => (
                <div key={i}>
                  <span style={{ color: '#888' }}>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                  {' - '}{entry.message}
                </div>
              ))}
            </div>
          )}
          {logsExpanded && (!state.botLogs || state.botLogs.length === 0) && (
            <div style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>Waiting for bot activity...</div>
          )}
        </div>
        <div className="form-group my-2">
          <input
            type="button"
            value="Update Appointment"
            className="btn btn-primary"
            onClick={onSubmit}
          />
          &nbsp;&nbsp;
          <input
            type="button"
            value="Cancel"
            className="btn btn-primary btn-cancel"
            onClick={onCancel}
          />
        </div>
      </form>
    </div>
  );
}

export default EditAppointment;